import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import Swal from "sweetalert2";
import { MdRefresh, MdContentCopy, MdUploadFile } from "react-icons/md";

import Container from "../../components/Container";
import Loading from "../../../home/components/Loading";
import { BrComerceApiService } from "../../../../services/BrComerceApiService";
import styles from "./styles.module.scss";

// Mesmo limite da API por requisicao; selecao maior vai em lotes.
const FILES_PER_REQUEST = 50;

const STATUS_LABEL = {
  registrada: "Registrada",
  duplicada: "Já estava registrada",
  recusada: "Recusada"
};

const formatDate = date => (date ? moment(date).format("DD/MM/YYYY HH:mm") : "-");

const formatMoney = value =>
  Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const shortKey = chave => (chave ? `${chave.slice(0, 6)}...${chave.slice(-6)}` : "-");

const escapeHtml = text =>
  String(text ?? "").replace(/[&<>"']/g, char => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;"
  })[char]);

const chunk = (list, size) =>
  Array.from({ length: Math.ceil(list.length / size) }, (_, index) =>
    list.slice(index * size, index * size + size)
  );

const MyNfePage = () => {
  window.setPageTitle("Notas fiscais");

  const api = new BrComerceApiService();
  const inputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.makeHttpRequest({
        method: "GET",
        url: "/nfe-documents/mine",
        params: { limit: 200 }
      });
      setDocuments(response?.data || response || []);
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Houve um erro ao carregar as notas fiscais."
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const copyKey = async chave => {
    try {
      await navigator.clipboard.writeText(chave);
      Swal.fire({
        icon: "success",
        title: "Chave copiada",
        timer: 1200,
        showConfirmButton: false
      });
    } catch (e) {
      Swal.fire({ icon: "info", title: "Chave da NF-e", text: chave });
    }
  };

  const showResults = results => {
    const refused = results.filter(result => result.status === "recusada");
    const accepted = results.length - refused.length;

    const rows = results
      .map(
        result => `
          <tr>
            <td>${escapeHtml(result.arquivo)}</td>
            <td>${escapeHtml(STATUS_LABEL[result.status] || result.status)}</td>
            <td>${escapeHtml(result.motivo || (result.vinculoStatus === "vinculada" ? "Vinculada ao envio" : "Aguardando o envio"))}</td>
          </tr>`
      )
      .join("");

    Swal.fire({
      icon: refused.length ? (accepted ? "warning" : "error") : "success",
      title: `${accepted} de ${results.length} nota(s) aceita(s)`,
      width: 760,
      html: `<div class="${styles.resultWrapper}"><table class="${styles.resultTable}">
        <thead><tr><th>Arquivo</th><th>Resultado</th><th>Detalhe</th></tr></thead>
        <tbody>${rows}</tbody></table></div>`
    });
  };

  const handleFiles = async event => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";

    if (!files.length) return;

    setUploading(true);

    const results = [];

    try {
      for (const batch of chunk(files, FILES_PER_REQUEST)) {
        const data = new FormData();
        batch.forEach(file => data.append("files", file));

        const response = await api.makeHttpRequest({
          method: "POST",
          url: "/nfe-documents/upload",
          data,
          timeout: 120000
        });

        results.push(...(response?.data || response || []));
      }

      showResults(results);
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Envio não concluído",
        text: e?.response?.data?.message || "Houve um erro ao enviar os XML, tente novamente."
      });

      if (results.length) showResults(results);
    } finally {
      setUploading(false);
      loadDocuments();
    }
  };

  const pendingCount = documents.filter(doc => doc.vinculoStatus === "pendente").length;

  return (
    <>
      <Loading isLoading={(loading && !documents.length) || uploading} />

      <Container title="Notas fiscais">
        <div className={styles.contentBody}>
          <div className={styles.infoCard}>
            <h5>NF-e dos seus envios</h5>
            <p>
              A partir de 01/11/2026, para contas contribuintes de ICMS, os Correios
              exigem a chave da NF-e em cada envio: sem ela, a pré-postagem é recusada.
              Envie aqui o XML das notas
              autorizadas. Nós vinculamos cada nota ao envio pelo número do pedido ou
              pela chave já informada no envio.
            </p>
          </div>

          <div className={styles.tableHeader}>
            <h5>
              Notas <span className={styles.counter}>{documents.length}</span>
              {pendingCount > 0 && (
                <span className={`${styles.counter} ${styles.counterPending}`}>
                  {pendingCount} aguardando envio
                </span>
              )}
            </h5>
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.btnRefresh}
                onClick={loadDocuments}
                title="Atualizar"
              >
                <MdRefresh size={20} />
                <span>Atualizar</span>
              </button>
              <button
                type="button"
                className={styles.btnUpload}
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
              >
                <MdUploadFile size={20} />
                <span>Enviar XML</span>
              </button>
              <input
                ref={inputRef}
                type="file"
                accept=".xml,text/xml,application/xml"
                multiple
                hidden
                onChange={handleFiles}
              />
            </div>
          </div>

          <div className={styles.contentTable}>
            <table className={styles.tableContainer}>
              <thead>
                <tr>
                  <th width="16%">Chave</th>
                  <th width="9%">NF / Série</th>
                  <th width="20%">Destinatário</th>
                  <th width="14%">Destino</th>
                  <th width="9%">Valor</th>
                  <th width="9%">Pedido</th>
                  <th width="11%">Situação</th>
                  <th width="12%">Enviada em</th>
                </tr>
              </thead>
              <tbody>
                {documents.length > 0
                  ? documents.map(doc => (
                      <tr key={doc.id}>
                        <td>
                          <button
                            type="button"
                            className={styles.keyButton}
                            onClick={() => copyKey(doc.chave)}
                            title={doc.chave}
                          >
                            <span>{shortKey(doc.chave)}</span>
                            <MdContentCopy size={14} />
                          </button>
                        </td>
                        <td>{`${doc.numero} / ${doc.serie}`}</td>
                        <td className={styles.ellipsisCell} title={doc.destinatarioNome}>
                          {doc.destinatarioNome}
                        </td>
                        <td>{`${doc.destinatarioMunicipio} / ${doc.destinatarioUf}`}</td>
                        <td>{formatMoney(doc.valorNota)}</td>
                        <td>{doc.numeroPedido || "-"}</td>
                        <td>
                          <span
                            className={`${styles.statusBadge} ${
                              doc.vinculoStatus === "vinculada" ? styles.statusLinked : styles.statusPending
                            }`}
                          >
                            {doc.vinculoStatus === "vinculada" ? "Vinculada" : "Aguardando envio"}
                          </span>
                        </td>
                        <td>{formatDate(doc.createdAt)}</td>
                      </tr>
                    ))
                  : !loading && (
                      <tr>
                        <td colSpan="8" className={styles.emptyCell}>
                          Nenhuma nota enviada ainda. Use "Enviar XML" para subir as NF-e
                          dos seus pedidos.
                        </td>
                      </tr>
                    )}
              </tbody>
            </table>
          </div>
        </div>
      </Container>
    </>
  );
};

export default MyNfePage;
